import React, { useMemo } from 'react';
import { AppBar, Box, Container, Typography, Button, Divider, Link as MuiLink, Toolbar } from '@mui/material';
import { ROUTES } from '@/constants';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Logo from '@components/Logo';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  const lastUpdatedLabel = useMemo(() => {
    const d = new Date();
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('pt-BR');
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logo />
          </Box>
          <Button variant="outlined" onClick={() => navigate(ROUTES.LOGIN)}>
            Entrar
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md">
        <Box sx={{ mb: 3, pt: { xs: 4, md: 6 } }}>
          <Button variant="text" onClick={() => navigate(ROUTES.HOME)}>
            Voltar para a página inicial
          </Button>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Política de Privacidade (LGPD)
        </Typography>

        {lastUpdatedLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Última atualização: {lastUpdatedLabel}
          </Typography>
        )}

        <Box
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: { xs: 2.5, md: 4 },
          }}
        >
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            A VOVER (“Vover”, “nós”) valoriza a privacidade e a proteção de dados pessoais. Esta Política descreve,
            de forma transparente, como tratamos dados pessoais no site e nos aplicativos Vover, em conformidade com
            a Lei nº 13.709/2018 (LGPD).
          </Typography>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            1. Papéis (Controlador e Operador)
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            - Quando você usa a Vover como usuário (conta, autenticação, suporte, cobrança), a Vover pode atuar como
            <strong> controladora</strong> de determinados dados.<br />
            - Quando sua organização insere dados de terceiros na plataforma (por exemplo, informações de condomínios,
            demandas e anexos), sua organização normalmente atua como <strong>controladora</strong> e a Vover como
            <strong> operadora</strong>, tratando dados conforme instruções contratuais e para prestar o serviço.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            2. Quais dados tratamos
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Dependendo do uso, podemos tratar:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.9 }}>
            <li>
              <strong>Dados de cadastro e conta</strong>: nome, e-mail, telefone, credenciais e preferências.
            </li>
            <li>
              <strong>Dados da organização</strong>: razão social, CNPJ, endereço e configurações.
            </li>
            <li>
              <strong>Dados operacionais</strong>: demandas, descrições, registros, comentários e informações vinculadas às rotinas do condomínio.
            </li>
            <li>
              <strong>Anexos</strong>: imagens/arquivos enviados para registro e gestão de demandas (quando habilitado).
            </li>
            <li>
              <strong>Dados de uso e segurança</strong>: IP, identificadores técnicos do dispositivo/navegador, registros técnicos e eventos necessários para prevenção a fraude e segurança.
            </li>
            <li>
              <strong>Dados de cobrança</strong>: informações relacionadas a faturas, pagamentos (Boleto/PIX) e NFS-e quando aplicável.
            </li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            3. Finalidades do tratamento
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.9 }}>
            <li>Prestar o serviço, operar funcionalidades e manter a plataforma disponível.</li>
            <li>Autenticar usuários, administrar contas e controlar acessos.</li>
            <li>Gerar relatórios, melhorar experiência e evoluir o produto (preferindo agregação/anonimização quando possível).</li>
            <li>Atender solicitações de suporte e comunicação operacional (e-mail/notificações quando habilitado).</li>
            <li>Processar cobranças, identificar pagamentos e cumprir obrigações fiscais quando aplicável.</li>
            <li>Prevenir fraudes, incidentes e garantir segurança da informação.</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            4. Bases legais (LGPD)
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            As bases legais podem incluir: <strong>execução de contrato</strong>, <strong>cumprimento de obrigação legal</strong>,
            <strong>legítimo interesse</strong> (ex.: segurança e prevenção a fraudes) e <strong>consentimento</strong> quando necessário.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            5. Compartilhamento com terceiros
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Não vendemos dados pessoais. Podemos compartilhar dados de forma limitada com:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.9 }}>
            <li>
              <strong>Provedores de infraestrutura</strong> (hospedagem, banco de dados, armazenamento e entrega de conteúdo), na medida necessária para operar o serviço.
            </li>
            <li>
              <strong>Serviços de cobrança/faturamento</strong> e instituições financeiras, para emissão de Boleto/PIX e gestão de pagamentos.
            </li>
            <li>
              <strong>Serviços de comunicação</strong> (e-mail/notificações), quando usados pelo produto.
            </li>
            <li>Autoridades competentes, quando houver obrigação legal ou ordem válida.</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            6. Cookies e tecnologias similares
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            Utilizamos cookies e mecanismos técnicos necessários para autenticação, segurança e funcionamento. Você pode gerenciar cookies pelo seu navegador,
            mas a desativação pode afetar o uso do sistema.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            7. Retenção e exclusão
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            Mantemos dados pelo tempo necessário para cumprir finalidades e obrigações legais/contratuais. Prazos podem variar conforme tipo de dado
            (ex.: registros fiscais, auditoria e segurança). Quando aplicável, realizamos exclusão, anonimização ou bloqueio conforme a LGPD.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            7.1. Como excluir sua conta (organização)
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            A exclusão é <strong>irreversível</strong> e remove os dados operacionais da organização. Somente o <strong>criador da conta</strong> pode concluir.
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.9 }}>
            <li>
              <strong>Web (sem estar logado)</strong>: na tela de login, clique em <strong>“Quero excluir minha conta”</strong>.
            </li>
            <li>
              <strong>Web (logado)</strong>: <strong>Configurações</strong> → <strong>Exclusão de conta</strong>.
            </li>
            <li>
              <strong>Mobile (sem estar logado)</strong>: na tela de login, toque em <strong>“Quero excluir minha conta”</strong>.
            </li>
            <li>
              <strong>Mobile (logado)</strong>: <strong>Configurações</strong> → <strong>Exclusão de conta</strong>.
            </li>
          </Box>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            O fluxo tem 3 etapas: (1) informar e-mail e senha do criador, (2) validar o código de 6 dígitos enviado por e-mail e (3) confirmar a palavra{' '}
            <strong>EXCLUIR</strong>. Após iniciar, o acesso ao sistema é bloqueado imediatamente e o progresso é exibido até a conclusão.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            8. Segurança
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
            Adotamos medidas técnicas e organizacionais para proteger dados (controle de acesso, segregação por organização, logs de auditoria,
            monitoramento e boas práticas de segurança). Nenhum sistema é 100% imune; em caso de incidentes relevantes, seguimos procedimentos de resposta e comunicação.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            9. Direitos do titular
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Você pode solicitar: confirmação de tratamento, acesso, correção, portabilidade, anonimização/bloqueio/eliminação, informação sobre compartilhamento e revogação de consentimento (quando aplicável).
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>
            10. Contato e canal para solicitações
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Para exercer direitos ou tirar dúvidas, use os canais de suporte da plataforma. Página de contato:{' '}
            <MuiLink component={RouterLink} to={ROUTES.CONTACT} underline="hover">
              vover.com.br/contato
            </MuiLink>
            . Se você precisa de um canal por e-mail, utilize:{' '}
            <MuiLink href="mailto:contato@vover.com.br">contato@vover.com.br</MuiLink>.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>
            11. Atualizações desta Política
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Podemos atualizar esta Política periodicamente. A versão vigente será publicada nesta página com a data de atualização.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;

